import { useSyncExternalStore } from "react";
import axios from "axios";
import store from "../redux/store";
import { API_BASE_URL } from "./apiConfig";
import { ROUTES_KEY } from "./savedRoutes";

// ═════════════════════════════════════════════════════════════════════
//  App routes from the database.
//
//  There is no route list in the code any more. Two sources, both in the DB:
//    · AppRoute table  (GET /api/app-routes, public) — login/landing/signup, Settings sub-paths, standalone pages
//    · the user's modules (login response / my-modules) — every sidebar module: route_path + page_key
//
//  loadAppRoutes() fetches the first one (the last answer is kept in localStorage, so the app starts at once and
//  refreshes in the background). routeOf("orders-key") answers "which URL is this page at" for code that needs to
//  link to a page — by AppRoute name ("login", "settings.master-settings") or by module page key ("admin.orders").
// ═════════════════════════════════════════════════════════════════════
const KEY = ROUTES_KEY;
let table = [];
try { table = JSON.parse(window.localStorage.getItem(KEY) || "[]"); } catch { table = []; }
let loaded = table.length > 0;
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());

export const appRoutesStore = {
  subscribe: (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
  get: () => table,
  isLoaded: () => loaded,
};
export const useAppRoutes = () => useSyncExternalStore(appRoutesStore.subscribe, appRoutesStore.get);
export const useAppRoutesLoaded = () => useSyncExternalStore(appRoutesStore.subscribe, appRoutesStore.isLoaded);

export async function loadAppRoutes() {
  try {
    const res = await axios.get(`${API_BASE_URL}/api/app-routes`, { timeout: 15000 });
    if (Array.isArray(res.data?.routes)) {
      table = res.data.routes;
      loaded = true;
      try { window.localStorage.setItem(KEY, JSON.stringify(table)); } catch { /* storage blocked */ }
      emit();
      return true;
    }
  } catch { /* offline / server down — keep the saved table */ }
  return loaded;
}

// URL of a page: an AppRoute by its name, else the logged-in user's module with that page key. "" when unknown.
export function routeOf(nameOrPageKey) {
  const r = table.find((x) => x.name === nameOrPageKey);
  if (r) return r.path;
  const m = (store.getState().auth?.modules || []).find((x) => x.page_key === nameOrPageKey);
  return m?.route_path || "";
}

// ── page URLs the app links to — all read from the database (AppRoute table / the user's modules), none written in code ──
const ROOT = "/"; // the site root is the only path that is not looked up
export const loginRoute = (adminArea = false) => routeOf(adminArea ? "admin-login" : "login") || ROOT;
export const signupRoute = () => routeOf("signup") || ROOT;
export const subscriptionRoute = () => routeOf("subscription") || ROOT;
// Dashboard page of a side: from the given modules (a login response) or the logged-in user's; the login page when there is none.
export function dashboardRoute(adminSide, modules) {
  const key = adminSide ? "admin.dashboard" : "customer.dashboard";
  const list = modules || store.getState().auth?.modules || [];
  return list.find((m) => m.page_key === key)?.route_path || loginRoute(adminSide);
}
export const accountRoute = (adminSide) => routeOf(adminSide ? "admin.account" : "customer.account");
